import React, { useState } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';

const DRIVER_TYPE_OPTIONS = [
  '$ / ARN',
  '$ / ARN LM',
  '$ / ARN LY',
  '$ / ORN',
  '$ / ORN LM',
  '$ / ORN LY',
  '$ LM',
  '$POR Projection',
  'Budget',
  'LY Same Month',
  '% of Revenue',
  '% of Selected Revenue',
  'REVPAR Projection',
  'Static Entry'
];

const BulkDriverEditModal = ({ 
  isOpen, 
  onClose, 
  selectedAccounts = [], 
  onSave 
}) => {
  const [driverType, setDriverType] = useState('');
  const [driverInput, setDriverInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!driverType) return;
    
    setIsSaving(true);
    try {
      await onSave({
        driverType,
        driverInput: parseFloat(driverInput) || 0
      });
      onClose();
    } catch (error) {
      console.error('Error saving bulk drivers:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const requiresInput = [
    '$ / ARN', 
    '$ / ORN', 
    'Total Guests', 
    '% of Revenue'
  ].includes(driverType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Bulk Edit Drivers</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <AlertCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              You are updating {selectedAccounts.length} accounts. This will overwrite their current forecast driver settings.
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forecast Driver
              </label>
              <select
                value={driverType}
                onChange={(e) => setDriverType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select a driver...</option>
                {DRIVER_TYPE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {requiresInput && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Driver Input {driverType === '% of Revenue' ? '(%)' : '($)'}
                </label>
                <input
                  type="number"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                  step={driverType === '% of Revenue' ? "0.01" : "1"}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!driverType || isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Apply to {selectedAccounts.length} Accounts
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDriverEditModal;

